'use client';
import React, { useEffect, useRef, useState } from 'react';
import BeforeAfterSlider from './BeforeAfterSlider';

const stages = [
{
  number: '01',
  badge: 'Stage 1',
  title: 'Decontamination Wash',
  description: 'pH-neutral pre-soak dissolves road film, brake dust, and industrial fallout. A two-bucket hand wash follows - no swirl-inducing automated brushes, ever.',
  detail: 'pH 7.0 foam · Iron fallout remover · Wheel decon · Rinse-less final pass',
  beforeSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM-1777425764872.jpeg",
  afterSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM-1777425764872.jpeg",
  beforeAlt: 'Car before decontamination wash showing road grime and brake dust',
  afterAlt: 'Car after decontamination wash showing clean surface',
  metric: { label: 'pH Level', value: '7.0', sub: 'vs. 11–13 at drive-throughs' },
  lightTemp: 'from-cockpit to-carbon-mid'
},
{
  number: '02',
  badge: 'Stage 2',
  title: 'Clay Bar Treatment',
  description: 'A detailing clay bar physically lifts embedded contaminants the wash cannot reach: overspray, rail dust, tree sap. Paint goes from sandpaper to glass.',
  detail: 'Fine-grade clay · Lubricated pass · Tactile check · Zero marring',
  beforeSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__1_-1777425764874.jpeg",
  afterSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__1_-1777425764874.jpeg",
  beforeAlt: 'Car interior before detailing showing dirt and wear',
  afterAlt: 'Car interior after clay bar treatment showing clean smooth surfaces',
  metric: { label: 'Surface Smoothness', value: '98%', sub: 'contaminants removed' },
  lightTemp: 'from-carbon-mid to-carbon'
},
{
  number: '03',
  badge: 'Stage 3',
  title: 'Paint Correction',
  description: 'Machine polishing removes swirl marks, buffer trails, and fine scratches down to the clear coat. We measure defect reduction with paint depth gauges - no guessing.',
  detail: 'DA polisher · 3-stage compound · Swirl removal · Clear coat safe',
  beforeSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__2_-1777425764876.jpeg",
  afterSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__2_-1777425764876.jpeg",
  beforeAlt: 'Car exterior before paint correction showing swirl marks and scratches',
  afterAlt: 'Car exterior after paint correction showing mirror-like finish',
  metric: { label: 'Defect Reduction', value: '92%', sub: 'measured by paint depth gauge' },
  lightTemp: 'from-carbon to-carbon'
},
{
  number: '04',
  badge: 'Stage 4',
  title: 'Ceramic Coat Application',
  description: 'SiO₂ ceramic coating bonds to corrected paint at a molecular level. 9H hardness, hydrophobic surface, and UV resistance for up to 6 years, guaranteed.',
  detail: '9H hardness · SiO₂ ceramic · 6-year warranty · Hydrophobic finish',
  beforeSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__3_-1777425764878.jpeg",
  afterSrc: "/assets/images/WhatsApp_Image_2026-04-28_at_10.21.59_PM__3_-1777425764878.jpeg",
  beforeAlt: 'Car before ceramic coating application showing unprotected paint',
  afterAlt: 'Car after ceramic coating showing hydrophobic water-beading finish',
  metric: { label: 'Coating Durability', value: '6 yrs', sub: 'backed by written warranty' },
  lightTemp: 'from-carbon to-cockpit'
}];


export default function RevealStagesSection() {
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const observers = stageRefs.current.map((ref, i) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveStage(i);
              entry.target.querySelectorAll('.reveal').forEach((el, idx) => {
                setTimeout(() => el.classList.add('visible'), idx * 120);
              });
            }
          });
        },
        { threshold: 0.35 }
      );
      obs.observe(ref);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, []);

  return (
    <section id="reveal-stages" className="py-24 md:py-32" style={{ background: 'var(--cockpit-black)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="metric-pill mb-5 reveal">The Correction Process</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(32px, 5vw, 60px)', letterSpacing: '-0.03em', lineHeight: 1 }}>
            
            Scroll is the<br />
            <span style={{ color: 'var(--ceramic-blue)' }}>correction.</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto reveal reveal-delay-2" style={{ color: 'var(--aluminum-dim)', fontSize: '17px', lineHeight: 1.6 }}>
            Each stage below mirrors what happens to your paint. Drag the slider to see the transformation.
          </p>
        </div>

        {/* Stage progress */}
        <div className="flex items-center justify-center gap-3 mb-20 reveal">
          {stages.map((s, i) =>
          <React.Fragment key={s.number}>
              <div className={`stage-dot ${i <= activeStage ? 'active' : ''}`} />
              {i < stages.length - 1 &&
            <div
              className="h-px w-12 md:w-20 transition-all duration-700"
              style={{ background: i < activeStage ? 'var(--ceramic-blue)' : 'var(--aluminum-faint)' }} />

            }
            </React.Fragment>
          )}
        </div>

        {/* Stages */}
        <div className="space-y-32">
          {stages.map((stage, i) =>
          <div
            key={stage.number}
            ref={(el) => {stageRefs.current[i] = el;}}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
            style={{ flexDirection: i % 2 === 1 ? 'row-reverse' : 'row' }}>
            
              {/* Text side */}
              <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="stage-badge reveal">{stage.badge}</div>
                <div
                className="font-manrope font-extrabold reveal reveal-delay-1 mt-3 mb-2"
                style={{ fontSize: 'clamp(52px, 8vw, 100px)', color: 'rgba(255,255,255,0.04)', letterSpacing: '-0.05em', lineHeight: 1, userSelect: 'none' }}>
                
                  {stage.number}
                </div>
                <h3
                className="font-manrope font-extrabold text-white reveal reveal-delay-1 -mt-6"
                style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                
                  {stage.title}
                </h3>
                <p
                className="mt-5 reveal reveal-delay-2 leading-relaxed"
                style={{ color: 'var(--aluminum-dim)', fontSize: '16px' }}>
                
                  {stage.description}
                </p>
                <div
                className="mt-6 reveal reveal-delay-3 rounded-xl px-5 py-4 flex items-start gap-4"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(63,63,70,0.6)' }}>
                
                  <div
                  className="shrink-0 w-1 rounded-full self-stretch"
                  style={{ background: 'var(--ceramic-blue)', minHeight: '32px' }} />
                
                  <span style={{ fontSize: '13px', color: 'var(--aluminum-dim)', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>
                    {stage.detail}
                  </span>
                </div>
                {/* Metric */}
                <div
                className="mt-8 reveal reveal-delay-4 inline-flex items-end gap-3 rounded-2xl px-6 py-5"
                style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}>
                
                  <div>
                    <div
                    className="font-manrope font-extrabold text-white"
                    style={{ fontSize: '36px', letterSpacing: '-0.04em', lineHeight: 1 }}>
                    
                      {stage.metric.value}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--ceramic-blue)', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' }}>
                      {stage.metric.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--aluminum-dim)', marginTop: '4px' }}>
                      {stage.metric.sub}
                    </div>
                  </div>
                </div>
              </div>

              {/* Slider side */}
              <div className={`reveal reveal-delay-2 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <BeforeAfterSlider
                beforeSrc={stage.beforeSrc}
                afterSrc={stage.afterSrc}
                beforeAlt={stage.beforeAlt}
                afterAlt={stage.afterAlt}
                initialPosition={40} />
              
                <p className="mt-3 text-center" style={{ fontSize: '12px', color: 'var(--aluminum-dim)', fontFamily: 'DM Sans, sans-serif' }}>
                  ← Drag to reveal transformation
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}